/**
 * Module-Federation loader. Loading the `chat-v3` remote is the only thing this
 * application does (UN-8304, T-51).
 *
 * What used to be here: Sentry init, a desktop/mobile split by hostname, and an
 * `import('./main.desktop')` that booted a whole Vue 2 application — router, Vuex,
 * plugins, the operator UI — which then rendered `ChatV3Root.vue` on `/v3/*` and
 * handed the remote six references to itself. All six are gone: the transport
 * (T-14), services (T-28), the store (T-23) and the router (T-32) are native in
 * chat-v3, its guards are its own (T-29/T-31/T-49), and Sentry is not being ported
 * (owner's decision). The mobile UI is switched off, so the hostname split has
 * nothing to choose between.
 *
 * Everything the page still needs from this repo lives outside this file:
 * `templates/index.ejs` (favicon, `app2 -> app` redirect), `index.js` (the
 * static server) and `vue.config.js` (the remote URL per environment).
 * Pingdom RUM and the New Relic agent both used to load from `index.ejs` too;
 * Pingdom was removed, not migrated (owner's decision, UN-8304), and New
 * Relic moved into chat-v3 itself, the same way Zendesk did in T-55 (owner's
 * decision, 2026-08-05) — see `newRelicAgent.service.ts` there.
 */

const CONTAINER_ID = 'app'

/** Carried over verbatim from `ChatV3Root.vue`, the wrapper this replaces. */
const FAILURE_MESSAGE = 'Failed to load chat module. Please try again later.'

const mountChatV3 = async (): Promise<void> => {
  const container = document.getElementById(CONTAINER_ID)

  if (!container) {
    // eslint-disable-next-line no-console
    console.error(`Cannot mount chat-v3: #${CONTAINER_ID} is missing from index.ejs`)

    return
  }

  /**
   * The remote sizes itself in `%` and cannot establish a height of its own — it
   * has several root nodes and no wrapper (`apps/chat-v3/src/MainLayout.vue`). Its
   * previous container was `.chat-v3-wrapper`, at `100vh` minus this app's footer;
   * there is no footer now. Without a height here the app mounts and renders
   * nothing, with no error anywhere.
   */
  container.style.height = '100vh'

  try {
    // eslint-disable-next-line import/no-unresolved, import/extensions
    const { bootstrap } = await import('chatV3/main')

    await bootstrap({ hostEl: container })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize ChatV3:', error)
    container.textContent = FAILURE_MESSAGE
  }
}

// eslint-disable-next-line @typescript-eslint/no-floating-promises
mountChatV3()
